/**
 * LTV-predict App-Settings sub-tabs (WS12-frontend). Ported 1:1 from the
 * design bundle (oneads-handoff-3 `app_settings_metrics.jsx`), wired to the
 * real /api/app-settings LTV endpoints via ltv-hooks. Only affects apps with
 * the LTV feature configured — every other app just shows empty state.
 */

import { useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/Badge";
import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import { useApps, useAppGoalTarget } from "@/lib/api-hooks";
import {
  LTV_FUNNEL_ROLES,
  LTV_ROLES,
  useCoefficientRows,
  useSubtypeBreakdown,
  useCommissionConfig,
  useLoadedCoefficients,
  useCustomMetricReadiness,
  useCustomMetrics,
  useDeleteCustomMetric,
  useDeleteSubscriptionPrice,
  usePriceMapping,
  useReplaceEventMapping,
  useReplacePriceMapping,
  useSetChosenSource,
  useSubscriptionConfig,
  useSubscriptionEventMapping,
  useSubscriptionPrices,
  useUpdateCommissionConfig,
  useUpdateSubscriptionAdvanced,
  useUpsertCustomMetric,
  useUpsertSubscriptionPrice,
  type AlgorithmRole,
  type CoefficientRow,
  type CoefficientSource,
  type CommissionConfig,
  type CustomMetric,
  type EventMappingRow,
  type PriceMappingRow,
  type SubscriptionAdvancedUpdate,
  type SubscriptionPrice,
} from "@/lib/ltv-hooks";
import { useHasRole } from "@/lib/useAuth";

import { StSectionHead } from "./_shared";
import { useStickyAppId } from "./useStickyAppId";

// ---------------------------------------------------------------------------
// Shared design-bundle controls (ported from app_settings_shared.jsx)
// ---------------------------------------------------------------------------
function AsToggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: 38,
        height: 22,
        borderRadius: 11,
        border: 0,
        padding: 2,
        flex: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        background: checked
          ? "var(--c-accent-500)"
          : "var(--c-border-strong, var(--c-divider))",
        transition: "background .18s var(--ease)",
        display: "inline-flex",
        justifyContent: checked ? "flex-end" : "flex-start",
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "var(--sh-1)",
        }}
      />
    </button>
  );
}

function AsCheckRow({
  checked,
  onChange,
  title,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  title: string;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        padding: "14px 0",
        borderBottom: "1px solid var(--c-divider)",
      }}
    >
      <AsToggle checked={checked} onChange={onChange} disabled={disabled} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600 }}>{title}</div>
        {hint && (
          <div
            className="muted"
            style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5, maxWidth: 660 }}
          >
            {hint}
          </div>
        )}
      </div>
    </div>
  );
}

function AsNumField({
  label,
  hint,
  value,
  onChange,
  step = "1",
  suffix,
  disabled,
  width = 120,
}: {
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
  step?: string;
  suffix?: string;
  disabled?: boolean;
  width?: number;
}) {
  return (
    <div className="field">
      <label className="label">{label}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          className="input mono"
          type="number"
          step={step}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value === "" ? 0 : +e.target.value)}
          style={{ width }}
        />
        {suffix && (
          <span className="muted" style={{ fontSize: 12 }}>
            {suffix}
          </span>
        )}
      </div>
      {hint && (
        <div
          className="muted"
          style={{ fontSize: 12, marginTop: 2, lineHeight: 1.45, maxWidth: 560 }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

function slugify(name: string): string {
  return (
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 64) || "metric"
  );
}

type Draft = {
  isNew: boolean;
  metric_key: string;
  display_name: string;
  counted_event_roles: AlgorithmRole[];
  observation_window_days: number;
  prediction_day: number;
};

const blankDraft = (): Draft => ({
  isNew: true,
  metric_key: "",
  display_name: "",
  counted_event_roles: [],
  observation_window_days: 14,
  prediction_day: 3,
});

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------
function MetricEditor({
  initial,
  trialDuration,
  goalDay,
  saving,
  onSave,
  onCancel,
}: {
  initial: Draft;
  trialDuration: number;
  goalDay: number | null;
  saving: boolean;
  onSave: (d: Draft) => void;
  onCancel: () => void;
}) {
  const [m, setM] = useState<Draft>(initial);
  const early = m.prediction_day < trialDuration;

  const toggle = (r: AlgorithmRole) =>
    setM((x) => ({
      ...x,
      counted_event_roles: x.counted_event_roles.includes(r)
        ? x.counted_event_roles.filter((e) => e !== r)
        : [...x.counted_event_roles, r],
    }));

  const setName = (v: string) =>
    setM((x) => ({
      ...x,
      display_name: v,
      metric_key: x.isNew ? slugify(v) : x.metric_key,
    }));

  return (
    <div
      className="card card-pad"
      style={{ marginBottom: 16, border: "1px solid var(--c-accent-200)" }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label className="label">Display name</label>
          <input
            className="input"
            placeholder="e.g. Early signal day 0"
            value={m.display_name}
            onChange={(e) => setName(e.target.value)}
          />
          {m.metric_key && (
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Key{" "}
              <span className="mono" style={{ color: "var(--c-text-2)" }}>
                {m.metric_key}
              </span>
              {m.isNew ? " · used in rule conditions as custom_metric:…" : " · fixed"}
            </div>
          )}
        </div>

        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label className="label">
            Events{" "}
            <span className="muted" style={{ fontWeight: 400 }}>
              · pick any number of funnel roles
            </span>
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {LTV_FUNNEL_ROLES.map((r) => {
              const on = m.counted_event_roles.includes(r);
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => toggle(r)}
                  className={"chip" + (on ? " active" : "")}
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11.5,
                    cursor: "pointer",
                  }}
                >
                  {on && <span style={{ marginRight: 4 }}>✓</span>}
                  {r}
                </button>
              );
            })}
          </div>
        </div>

        <div className="field">
          <label className="label">Observation window (days)</label>
          <input
            className="input mono"
            type="number"
            value={m.observation_window_days}
            onChange={(e) =>
              setM({ ...m, observation_window_days: +e.target.value })
            }
            style={{ width: 120 }}
          />
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            How many days after install we collect events.
          </div>
        </div>
        <div className="field">
          <label className="label">Prediction day</label>
          <input
            className="input mono"
            type="number"
            value={m.prediction_day}
            onChange={(e) => setM({ ...m, prediction_day: +e.target.value })}
            style={{ width: 120 }}
          />
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            Cohort life-day used to compute the predict.
          </div>
        </div>
      </div>

      {/* predict method preview */}
      <div
        style={{
          marginTop: 16,
          padding: 16,
          borderRadius: 10,
          background: early ? "var(--c-warn-50)" : "var(--c-pos-50)",
          border: `1px solid ${early ? "var(--c-warn-200, #f0d894)" : "var(--c-pos-200, #b9e6c5)"}`,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: ".05em",
            color: "var(--c-text-3)",
            marginBottom: 8,
          }}
        >
          Predict method preview · day {m.prediction_day}
        </div>
        {early ? (
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            Trials on day {m.prediction_day} are <b>not yet converted</b>.
            <br />
            <span className="mono" style={{ fontSize: 12 }}>
              predict = count(trials) × avg_price × [trial→paid conversion] ×
              [renewal to goal_day]
            </span>
            <br />
            <span style={{ color: "var(--c-warn-600, #b45309)" }}>
              ⚠ Uses a conversion coefficient (trial→paid).
            </span>
          </div>
        ) : (
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            On day {m.prediction_day} trials are <b>converted</b> — paying users
            are real.
            <br />
            <span className="mono" style={{ fontSize: 12 }}>
              predict = revenue(payers) × [renewal to goal_day]
            </span>
            <br />
            <span style={{ color: "var(--c-pos-600)" }}>
              ✓ Conversion is already in the factual data.
            </span>
          </div>
        )}
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--c-text-3)" }}>
          Goal day{" "}
          <b style={{ color: "var(--c-text-2)" }}>
            {goalDay != null ? "D" + goalDay : "— set in Coefficients & Goals"}
          </b>{" "}
          · coefficients resolve per subtype, keyword level overrides country.
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginTop: 16,
        }}
      >
        <Btn onClick={onCancel}>Cancel</Btn>
        <Btn
          variant="primary"
          disabled={
            saving || !m.display_name || m.counted_event_roles.length === 0
          }
          onClick={() => onSave(m)}
        >
          {saving ? "Saving…" : "Save metric"}
        </Btn>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One metric card (its own component so readiness is fetched per metric)
// ---------------------------------------------------------------------------
function MetricCard({
  appId,
  m,
  goalDay,
  onEdit,
  onDelete,
  deleting,
}: {
  appId: number;
  m: CustomMetric;
  goalDay: number | null;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const readiness = useCustomMetricReadiness(appId, m.metric_key);
  const r = readiness.data;
  const gaps = r && !r.ready ? r.missing : null;

  return (
    <div className="card card-pad">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 16,
          alignItems: "flex-start",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 14.5, fontWeight: 600 }}>
              {m.display_name}
            </div>
            {r == null ? null : gaps ? (
              <Badge kind="warn">
                <span className="dot" />
                Incomplete
              </Badge>
            ) : (
              <Badge kind="enabled">
                <span className="dot" />
                Ready
              </Badge>
            )}
          </div>
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              marginTop: 10,
            }}
          >
            {m.counted_event_roles.map((e) => (
              <span
                key={e}
                className="mono"
                style={{
                  fontSize: 11,
                  padding: "2px 7px",
                  borderRadius: 6,
                  background: "var(--c-surface-sunk)",
                  color: "var(--c-text-2)",
                }}
              >
                {e}
              </span>
            ))}
          </div>
          <div
            className="muted"
            style={{ fontSize: 12.5, marginTop: 10, display: "flex", gap: 18 }}
          >
            <span>
              Window{" "}
              <b style={{ color: "var(--c-text-2)" }}>
                {m.observation_window_days}d
              </b>
            </span>
            <span>
              Prediction day{" "}
              <b style={{ color: "var(--c-text-2)" }}>D{m.prediction_day}</b>
            </span>
            <span>
              Goal day{" "}
              <b style={{ color: "var(--c-text-2)" }}>
                {goalDay != null ? "D" + goalDay : "—"}
              </b>
            </span>
          </div>
          {gaps && (
            <div
              style={{
                marginTop: 10,
                fontSize: 12,
                color: "var(--c-warn-600, #b45309)",
                lineHeight: 1.5,
              }}
            >
              To use this metric, complete: {gaps.join(" · ")}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, flex: "none" }}>
          <Btn size="sm" icon={I.edit} onClick={onEdit}>
            Edit
          </Btn>
          <Btn
            size="sm"
            variant="danger"
            icon={I.trash}
            disabled={deleting}
            onClick={onDelete}
          >
            Delete
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------
export function CustomMetricsSection() {
  const apps = useApps();
  const [appId, setAppId] = useStickyAppId(apps.data);

  const metricsQ = useCustomMetrics(appId);
  const priceQ = usePriceMapping(appId);
  const goalTargetQ = useAppGoalTarget(appId);
  const upsert = useUpsertCustomMetric();
  const del = useDeleteCustomMetric();

  const [editing, setEditing] = useState<Draft | null>(null);

  const trialDuration = useMemo(() => {
    const trials = (priceQ.data ?? [])
      .filter((p) => p.has_trial && p.trial_duration_days != null)
      .map((p) => p.trial_duration_days as number);
    return trials.length ? Math.min(...trials) : 3;
  }, [priceQ.data]);
  const goalDay = goalTargetQ.data?.goal_day ?? null;

  const metrics = metricsQ.data ?? [];

  const save = (d: Draft) => {
    if (appId == null) return;
    upsert.mutate(
      {
        app_id: appId,
        metric_key: d.metric_key || slugify(d.display_name),
        display_name: d.display_name,
        counted_event_roles: d.counted_event_roles,
        observation_window_days: d.observation_window_days,
        prediction_day: d.prediction_day,
      },
      { onSuccess: () => setEditing(null) },
    );
  };

  const removeMetric = (m: CustomMetric) => {
    if (appId == null) return;
    del.mutate({ app_id: appId, metric_key: m.metric_key });
  };

  const toDraft = (m: CustomMetric): Draft => ({
    isNew: false,
    metric_key: m.metric_key,
    display_name: m.display_name,
    counted_event_roles: m.counted_event_roles,
    observation_window_days: m.observation_window_days,
    prediction_day: m.prediction_day,
  });

  return (
    <>
      <StSectionHead
        title="Custom metrics"
        desc="Create any number of prediction metrics from your event funnel. Each metric defines the events collected, an observation window, and the cohort day at which the predict is computed."
        right={
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <select
              className="select"
              value={appId ?? ""}
              onChange={(e) => {
                setEditing(null);
                setAppId(e.target.value ? Number(e.target.value) : null);
              }}
              style={{ minWidth: 200 }}
            >
              <option value="">— pick app —</option>
              {(apps.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name ?? a.bundle_id}
                </option>
              ))}
            </select>
            {appId != null && !editing && (
              <Btn
                variant="primary"
                icon={I.plus}
                onClick={() => setEditing(blankDraft())}
              >
                New metric
              </Btn>
            )}
          </div>
        }
      />

      {appId == null && (
        <div className="card card-pad muted" style={{ textAlign: "center" }}>
          Pick an app to configure custom metrics.
        </div>
      )}

      {appId != null && editing && (
        <MetricEditor
          initial={editing}
          trialDuration={trialDuration}
          goalDay={goalDay}
          saving={upsert.isPending}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}

      {appId != null && !editing && metrics.length === 0 && (
        <div
          className="card card-pad"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            minHeight: 200,
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "var(--c-surface-sunk)",
              display: "grid",
              placeItems: "center",
              color: "var(--c-text-3)",
            }}
          >
            {I.sliders}
          </div>
          <div style={{ fontSize: 13.5, fontWeight: 500 }}>
            No custom metrics yet
          </div>
          <div className="muted" style={{ fontSize: 12.5 }}>
            Nothing is required until you create your first metric.
          </div>
        </div>
      )}

      {appId != null && !editing && metrics.length > 0 && (
        <div className="stack stack-16">
          {metrics.map((m) => (
            <MetricCard
              key={m.metric_key}
              appId={appId}
              m={m}
              goalDay={goalDay}
              deleting={del.isPending}
              onEdit={() => setEditing(toDraft(m))}
              onDelete={() => removeMetric(m)}
            />
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Advanced — enablement flags + learning / tail thresholds (admin only)
// ---------------------------------------------------------------------------
const ADV_NUM_KEYS = [
  "min_payers_threshold",
  "ci_threshold_pct",
  "divergence_threshold_pct",
  "learning_min_events_threshold",
  "min_trials_for_factual_conv",
  "tail_handoff_week",
  "min_alive_for_own_tail",
  "max_censoring_pct",
] as const;
const ADV_BOOL_KEYS = [
  "adjust_go_to_goal_enabled",
  "subscription_type_detection_enabled",
  "auto_coefficient_learning_enabled",
] as const;

type AdvDraft = Pick<
  import("@/lib/ltv-hooks").SubscriptionConfig,
  (typeof ADV_NUM_KEYS)[number] | (typeof ADV_BOOL_KEYS)[number]
>;

export function LtvAdvancedSection() {
  const apps = useApps();
  const [appId, setAppId] = useStickyAppId(apps.data);

  const isAdmin = useHasRole("admin");
  const cfgQ = useSubscriptionConfig(appId);
  const update = useUpdateSubscriptionAdvanced();

  const [draft, setDraft] = useState<AdvDraft | null>(null);
  const cfg = cfgQ.data ?? null;
  useEffect(() => {
    setDraft(null); // reset on app switch; reseed from the fresh config below
  }, [appId]);
  useEffect(() => {
    if (cfg && draft === null) {
      const d: Record<string, unknown> = {};
      [...ADV_NUM_KEYS, ...ADV_BOOL_KEYS].forEach((k) => (d[k] = cfg[k]));
      setDraft(d as AdvDraft);
    }
  }, [cfg, draft]);

  const dirty = useMemo(() => {
    if (!cfg || !draft) return false;
    return [...ADV_NUM_KEYS, ...ADV_BOOL_KEYS].some((k) => draft[k] !== cfg[k]);
  }, [cfg, draft]);

  const setBool = (k: (typeof ADV_BOOL_KEYS)[number], v: boolean) =>
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  const setNum = (k: (typeof ADV_NUM_KEYS)[number], v: number) =>
    setDraft((d) => (d ? { ...d, [k]: v } : d));

  const save = () => {
    if (appId == null || !draft || !cfg) return;
    const diff: SubscriptionAdvancedUpdate = {};
    [...ADV_NUM_KEYS, ...ADV_BOOL_KEYS].forEach((k) => {
      if (draft[k] !== cfg[k]) (diff as Record<string, unknown>)[k] = draft[k];
    });
    update.mutate({ app_id: appId, ...diff });
  };

  const d = draft;

  return (
    <>
      <StSectionHead
        title="Advanced"
        desc="Feature enablement, auto-learning, subscription-type detection and tail-extrapolation thresholds. Admins can tune these without a release."
        right={
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {isAdmin && dirty && (
              <Btn
                variant="primary"
                disabled={update.isPending}
                onClick={save}
              >
                {update.isPending ? "Saving…" : "Save changes"}
              </Btn>
            )}
            <select
              className="select"
              value={appId ?? ""}
              onChange={(e) =>
                setAppId(e.target.value ? Number(e.target.value) : null)
              }
              style={{ minWidth: 200 }}
            >
              <option value="">— pick app —</option>
              {(apps.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name ?? a.bundle_id}
                </option>
              ))}
            </select>
          </div>
        }
      />

      {appId == null && (
        <div className="card card-pad muted" style={{ textAlign: "center" }}>
          Pick an app to configure advanced settings.
        </div>
      )}

      {appId != null && !isAdmin && (
        <div
          className="card card-pad"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            minHeight: 220,
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "var(--c-surface-sunk)",
              display: "grid",
              placeItems: "center",
              color: "var(--c-text-3)",
            }}
          >
            {I.key}
          </div>
          <div style={{ fontSize: 13.5, fontWeight: 500 }}>
            Admin access required
          </div>
          <div
            className="muted"
            style={{ fontSize: 12.5, textAlign: "center", maxWidth: 360 }}
          >
            These settings are restricted to admins. Ask an administrator to
            change them.
          </div>
        </div>
      )}

      {appId != null && isAdmin && d && (
        <>
          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 2 }}>
              Enablement
            </div>
            <AsCheckRow
              checked={d.adjust_go_to_goal_enabled}
              onChange={(v) => setBool("adjust_go_to_goal_enabled", v)}
              title="Enable LTV-predict Go-to-Goal (Adjust)"
              hint="Master switch. When on, Go-to-Goal rules on this app compute the fact from the predicted LTV of the cohort (per-subtype coefficients) instead of the default projection. Off = unchanged behaviour."
            />
            <AsCheckRow
              checked={d.auto_coefficient_learning_enabled}
              onChange={(v) => setBool("auto_coefficient_learning_enabled", v)}
              title="Enable auto-learning of coefficients"
              hint="Once a week we recompute coefficients on your real cohorts. If a segment has enough matured payers with acceptable confidence, the algorithm uses your data instead of the uploaded table."
            />
            <AsCheckRow
              checked={d.subscription_type_detection_enabled}
              onChange={(v) => setBool("subscription_type_detection_enabled", v)}
              title="Enable subscription type detection"
              hint="Classify payers by subscription type (weekly / monthly / yearly × trial) and use per-subtype renewal coefficients. Requires the price ranges + prices."
            />
          </div>

          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 14 }}>
              Auto-learning thresholds
            </div>
            <div
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}
            >
              <AsNumField
                label="Minimum matured payers"
                value={d.min_payers_threshold}
                onChange={(v) => setNum("min_payers_threshold", v)}
                hint="Minimum matured payers for a segment to use its own coefficient."
              />
              <AsNumField
                label="Confidence interval threshold"
                suffix="%"
                step="0.1"
                value={d.ci_threshold_pct}
                onChange={(v) => setNum("ci_threshold_pct", v)}
                hint="Confidence interval width allowed before a segment's own coefficient is trusted."
              />
              <AsNumField
                label="Divergence alert threshold"
                suffix="%"
                step="0.1"
                value={d.divergence_threshold_pct}
                onChange={(v) => setNum("divergence_threshold_pct", v)}
                hint="How far Factual / Predict may diverge from Loaded before notifying."
              />
              <AsNumField
                label="Minimum paying users to start learning"
                value={d.learning_min_events_threshold}
                onChange={(v) => setNum("learning_min_events_threshold", v)}
                hint="Minimum unique paying users (by first payment) accumulated on a segment (country / keyword) before it starts learning its own coefficient. Counts distinct users, not events. Renewals don't increment."
              />
              <AsNumField
                label="Min trials for factual conversion"
                value={d.min_trials_for_factual_conv}
                onChange={(v) => setNum("min_trials_for_factual_conv", v)}
                hint="Minimum trials on a segment before offering to switch trial→paid from loaded to factual."
              />
            </div>
          </div>

          <div className="card card-pad">
            <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 6 }}>
              Tail extrapolation
            </div>
            <div
              className="muted"
              style={{ fontSize: 12.5, lineHeight: 1.55, maxWidth: 680 }}
            >
              Extrapolation predicts long-term LTV (1–2 years) from the first
              ~8 weeks of a cohort plus a reference tail curve, instead of
              waiting for cohorts to mature.
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 18,
                marginTop: 14,
              }}
            >
              <AsNumField
                label="Tail handoff week"
                value={d.tail_handoff_week}
                onChange={(v) => setNum("tail_handoff_week", v)}
                hint="Week from which the cohort tail is extrapolated using the reference tail curve."
              />
              <AsNumField
                label="Min alive for own tail"
                value={d.min_alive_for_own_tail}
                onChange={(v) => setNum("min_alive_for_own_tail", v)}
                hint="Min alive subscriptions at handoff week for a segment to build its own tail; below this it inherits the reference tail."
              />
              <AsNumField
                label="Max censoring"
                suffix="%"
                step="0.1"
                value={d.max_censoring_pct}
                onChange={(v) => setNum("max_censoring_pct", v)}
                hint="Max share of immature cohorts allowed for extrapolation."
              />
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Subscriptions — price-mapping ranges + retail prices + commission/SBP
// ---------------------------------------------------------------------------
type PmDraft = PriceMappingRow & { cid: string };
type PriceDraft = SubscriptionPrice & { cid: string };

let _cid = 0;
const nextCid = () => `r${_cid++}`;

function PriceMappingCard({ appId }: { appId: number }) {
  const q = usePriceMapping(appId);
  const replace = useReplacePriceMapping();
  const [rows, setRows] = useState<PmDraft[] | null>(null);
  useEffect(() => setRows(null), [appId]);
  useEffect(() => {
    if (q.data && rows === null) {
      setRows(q.data.map((r) => ({ ...r, cid: nextCid() })));
    }
  }, [q.data, rows]);

  const r = rows ?? [];
  const set = (cid: string, patch: Partial<PriceMappingRow>) =>
    setRows((x) => (x ? x.map((row) => (row.cid === cid ? { ...row, ...patch } : row)) : x));
  const add = () =>
    setRows((x) => [
      ...(x ?? []),
      { cid: nextCid(), billing_period: "weekly", has_trial: false, price_min: 0, price_max: 9.99, trial_duration_days: null },
    ]);
  const del = (cid: string) => setRows((x) => (x ? x.filter((row) => row.cid !== cid) : x));

  const dirty = useMemo(() => {
    if (!q.data || !rows) return false;
    return JSON.stringify(q.data) !== JSON.stringify(rows.map(({ cid: _omit, ...rest }) => rest));
  }, [q.data, rows]);

  const save = () =>
    replace.mutate({
      app_id: appId,
      rows: r.map(({ cid: _omit, ...rest }) => rest),
    });

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-head" style={{ padding: "14px 20px" }}>
        <h3>Price ranges (price_split)</h3>
        <span className="muted">
          How a payer&apos;s net price maps to a billing period + trial flag.
        </span>
      </div>
      <div className="table-scroll">
        <table className="dt" style={{ minWidth: 760 }}>
          <thead>
            <tr>
              <th>Billing period</th>
              <th>Has trial</th>
              <th className="num">Price min (net USD)</th>
              <th className="num">Price max</th>
              <th className="num">Trial days</th>
              <th style={{ width: 50 }} />
            </tr>
          </thead>
          <tbody>
            {r.map((row) => (
              <tr key={row.cid}>
                <td>
                  <select
                    className="select"
                    value={row.billing_period}
                    onChange={(e) => set(row.cid, { billing_period: e.target.value })}
                    style={{ height: 30, width: 120 }}
                  >
                    {["weekly", "monthly", "quarterly", "yearly", "lifetime"].map((p) => (
                      <option key={p}>{p}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    className="select"
                    value={row.has_trial ? "yes" : "no"}
                    onChange={(e) => set(row.cid, { has_trial: e.target.value === "yes" })}
                    style={{ height: 30, width: 80 }}
                  >
                    <option value="yes">yes</option>
                    <option value="no">no</option>
                  </select>
                </td>
                <td className="num">
                  <input
                    className="input mono"
                    type="number"
                    step="0.01"
                    value={row.price_min}
                    onChange={(e) => set(row.cid, { price_min: +e.target.value })}
                    style={{ width: 110, textAlign: "right" }}
                  />
                </td>
                <td className="num">
                  <input
                    className="input mono"
                    type="number"
                    step="0.01"
                    value={row.price_max}
                    onChange={(e) => set(row.cid, { price_max: +e.target.value })}
                    style={{ width: 110, textAlign: "right" }}
                  />
                </td>
                <td className="num">
                  {row.has_trial ? (
                    <input
                      className="input mono"
                      type="number"
                      value={row.trial_duration_days ?? 0}
                      onChange={(e) => set(row.cid, { trial_duration_days: +e.target.value })}
                      style={{ width: 80, textAlign: "right" }}
                    />
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td style={{ textAlign: "right" }}>
                  <Btn size="sm" variant="danger" icon={I.trash} onClick={() => del(row.cid)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div
        style={{
          padding: "14px 20px",
          borderTop: "1px solid var(--c-divider)",
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <Btn size="sm" icon={I.plus} onClick={add}>
          Add row
        </Btn>
        {dirty && (
          <Btn variant="primary" disabled={replace.isPending} onClick={save}>
            {replace.isPending ? "Saving…" : "Save ranges"}
          </Btn>
        )}
      </div>
    </div>
  );
}

function PricesCard({ appId }: { appId: number }) {
  const q = useSubscriptionPrices(appId);
  const upsert = useUpsertSubscriptionPrice();
  const del = useDeleteSubscriptionPrice();
  const [rows, setRows] = useState<PriceDraft[] | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => setRows(null), [appId]);
  useEffect(() => {
    if (q.data && rows === null) {
      setRows(q.data.map((r) => ({ ...r, cid: nextCid() })));
    }
  }, [q.data, rows]);

  const r = rows ?? [];
  const set = (cid: string, patch: Partial<SubscriptionPrice>) =>
    setRows((x) => (x ? x.map((row) => (row.cid === cid ? { ...row, ...patch } : row)) : x));
  const add = () =>
    setRows((x) => [...(x ?? []), { cid: nextCid(), subscription_ref: "", country_code: "DEFAULT", price_usd: 0 }]);
  const delRow = (cid: string) => setRows((x) => (x ? x.filter((row) => row.cid !== cid) : x));

  const refsWithDefault = new Set(r.filter((x) => x.country_code === "DEFAULT").map((x) => x.subscription_ref));
  const missingDefault = [...new Set(r.map((x) => x.subscription_ref).filter(Boolean))].filter(
    (ref) => !refsWithDefault.has(ref),
  );

  const dirty = useMemo(() => {
    if (!q.data || !rows) return false;
    return JSON.stringify(q.data) !== JSON.stringify(rows.map(({ cid: _omit, ...rest }) => rest));
  }, [q.data, rows]);

  const save = async () => {
    const orig = q.data ?? [];
    setSaving(true);
    try {
      const keep = new Set<string>();
      for (const row of r) {
        if (!row.subscription_ref || !row.country_code) continue;
        keep.add(row.subscription_ref + "|" + row.country_code);
        await upsert.mutateAsync({
          app_id: appId,
          subscription_ref: row.subscription_ref,
          country_code: row.country_code,
          price_usd: row.price_usd,
        });
      }
      for (const o of orig) {
        if (!keep.has(o.subscription_ref + "|" + o.country_code)) {
          await del.mutateAsync({
            app_id: appId,
            subscription_ref: o.subscription_ref,
            country_code: o.country_code,
          });
        }
      }
      setRows(null); // reseed from the refetched list
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-head" style={{ padding: "14px 20px" }}>
        <h3>Retail prices</h3>
        <span className="muted">
          Net price per (reference, country). Every reference needs a DEFAULT row.
        </span>
      </div>
      {missingDefault.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 20px",
            background: "var(--c-neg-50)",
            color: "var(--c-neg-600)",
            fontSize: 13,
            borderTop: "1px solid var(--c-divider)",
          }}
        >
          <span style={{ flex: "none" }}>{I.x}</span>
          <span>
            Missing DEFAULT price for: <b>{missingDefault.join(", ")}</b>. These
            references won&apos;t have an early-predict fallback.
          </span>
        </div>
      )}
      <div className="table-scroll">
        <table className="dt" style={{ minWidth: 560 }}>
          <thead>
            <tr>
              <th>Subscription ref</th>
              <th>Country</th>
              <th className="num">Price (USD)</th>
              <th style={{ width: 50 }} />
            </tr>
          </thead>
          <tbody>
            {r.map((row) => {
              const bad = missingDefault.includes(row.subscription_ref);
              return (
                <tr key={row.cid} style={bad ? { background: "var(--c-neg-50)" } : undefined}>
                  <td>
                    <input
                      className="input mono"
                      value={row.subscription_ref}
                      placeholder="weekly_trial"
                      onChange={(e) => set(row.cid, { subscription_ref: e.target.value })}
                      style={{ width: 170 }}
                    />
                  </td>
                  <td>
                    <input
                      className="input mono"
                      value={row.country_code}
                      onChange={(e) => set(row.cid, { country_code: e.target.value.toUpperCase() })}
                      style={{ width: 120, fontWeight: row.country_code === "DEFAULT" ? 600 : 400 }}
                    />
                  </td>
                  <td className="num">
                    <input
                      className="input mono"
                      type="number"
                      step="0.01"
                      value={row.price_usd}
                      onChange={(e) => set(row.cid, { price_usd: +e.target.value })}
                      style={{ width: 110, textAlign: "right" }}
                    />
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <Btn size="sm" variant="danger" icon={I.trash} onClick={() => delRow(row.cid)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div
        style={{
          padding: "14px 20px",
          borderTop: "1px solid var(--c-divider)",
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <Btn size="sm" icon={I.plus} onClick={add}>
          Add row
        </Btn>
        {dirty && (
          <Btn variant="primary" disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Save prices"}
          </Btn>
        )}
      </div>
    </div>
  );
}

function CommissionCard({ appId }: { appId: number }) {
  const q = useCommissionConfig(appId);
  const update = useUpdateCommissionConfig();
  const [draft, setDraft] = useState<CommissionConfig | null>(null);
  useEffect(() => setDraft(null), [appId]);
  useEffect(() => {
    if (q.data && draft === null) setDraft(q.data);
  }, [q.data, draft]);

  const d = draft;
  const dirty = useMemo(
    () => !!q.data && !!draft && JSON.stringify(q.data) !== JSON.stringify(draft),
    [q.data, draft],
  );
  const set = (patch: Partial<CommissionConfig>) => setDraft((x) => (x ? { ...x, ...patch } : x));

  if (!d) return null;

  return (
    <div className="card card-pad" style={{ maxWidth: 640 }}>
      <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 2 }}>Commission / SBP</div>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 6 }}>
        Apple Small Business Program enrolment. Used only for the day-0/1
        predict fallback — real revenue comes net from your provider.
      </div>
      <AsCheckRow
        checked={d.sbp_enrolled}
        onChange={(v) => set({ sbp_enrolled: v })}
        title="Enrolled in Apple Small Business Program"
        hint="When enrolled, Apple takes 15% instead of 30% — the fallback predict uses the lower rate between the entry and exit dates."
      />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 18 }}>
        <div className="field">
          <label className="label">SBP entry date</label>
          <input
            className="input"
            type="date"
            value={d.sbp_entry_date ?? ""}
            disabled={!d.sbp_enrolled}
            onChange={(e) => set({ sbp_entry_date: e.target.value || null })}
          />
        </div>
        <div className="field">
          <label className="label">
            SBP exit date{" "}
            <span className="muted" style={{ fontWeight: 400 }}>
              · optional
            </span>
          </label>
          <input
            className="input"
            type="date"
            value={d.sbp_exit_date ?? ""}
            disabled={!d.sbp_enrolled}
            onChange={(e) => set({ sbp_exit_date: e.target.value || null })}
          />
        </div>
      </div>
      {dirty && (
        <div style={{ marginTop: 16 }}>
          <Btn
            variant="primary"
            disabled={update.isPending}
            onClick={() => update.mutate({ app_id: appId, ...draft })}
          >
            {update.isPending ? "Saving…" : "Save commission"}
          </Btn>
        </div>
      )}
    </div>
  );
}

// Shared app picker + empty-state prompt for the three Subscriptions screens.
function SubAppPicker({
  apps,
  appId,
  onChange,
}: {
  apps: { id: number; name?: string | null; bundle_id?: string }[];
  appId: number | null;
  onChange: (id: number | null) => void;
}) {
  return (
    <select
      className="select"
      value={appId ?? ""}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      style={{ minWidth: 200 }}
    >
      <option value="">— pick app —</option>
      {apps.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name ?? a.bundle_id}
        </option>
      ))}
    </select>
  );
}

function SubPickPrompt({ text }: { text: string }) {
  return (
    <div className="card card-pad muted" style={{ textAlign: "center" }}>
      {text}
    </div>
  );
}

export function SubscriptionsSection() {
  const apps = useApps();
  const [appId, setAppId] = useStickyAppId(apps.data);
  return (
    <>
      <StSectionHead
        title="Subscriptions"
        desc="Price-mapping ranges (price_split) — the input subscription-type detection uses to classify each payer's plan (billing period × trial) from the paid amount."
        right={<SubAppPicker apps={apps.data ?? []} appId={appId} onChange={setAppId} />}
      />
      {appId == null ? (
        <SubPickPrompt text="Pick an app to configure the price-mapping ranges." />
      ) : (
        <PriceMappingCard appId={appId} />
      )}
    </>
  );
}

export function SubscriptionPricesSection() {
  const apps = useApps();
  const [appId, setAppId] = useStickyAppId(apps.data);
  return (
    <>
      <StSectionHead
        title="Subscription prices"
        desc="Net retail price per (reference, country), after Apple commission. Used only for early prediction of trial subscriptions — when the prediction day falls before the trial ends and there's no real payment yet, the system uses this average check to estimate future revenue. Not used for no-trial subscriptions (real revenue already exists on the prediction day). Every reference must have a DEFAULT row — the fallback when a country-specific price is missing."
        right={<SubAppPicker apps={apps.data ?? []} appId={appId} onChange={setAppId} />}
      />
      {appId == null ? (
        <SubPickPrompt text="Pick an app to configure retail prices." />
      ) : (
        <PricesCard appId={appId} />
      )}
    </>
  );
}

export function CommissionSbpSection() {
  const apps = useApps();
  const [appId, setAppId] = useStickyAppId(apps.data);
  return (
    <>
      <StSectionHead
        title="Commission / SBP"
        desc="Small Business Program enrolment window — sets the Apple commission (15% vs 30%) used when converting retail prices to net for the average-check fallback."
        right={<SubAppPicker apps={apps.data ?? []} appId={appId} onChange={setAppId} />}
      />
      {appId == null ? (
        <SubPickPrompt text="Pick an app to configure commission / SBP." />
      ) : (
        <CommissionCard appId={appId} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Event mapping — provider event name → algorithm role (per app, provider-
// agnostic). Content is the real event NAME (Code_Fixes §1), not tokens.
// ---------------------------------------------------------------------------
export function LtvEventMappingSection() {
  const apps = useApps();
  const [appId, setAppId] = useStickyAppId(apps.data);

  const q = useSubscriptionEventMapping(appId);
  const replace = useReplaceEventMapping();
  const [rows, setRows] = useState<EventMappingRow[] | null>(null);
  useEffect(() => setRows(null), [appId]);
  useEffect(() => {
    if (q.data && rows === null) setRows(q.data.map((r) => ({ ...r })));
  }, [q.data, rows]);

  const r = rows ?? [];
  const provider = r.length ? r[0].provider : null;
  const dirty = useMemo(
    () => !!q.data && !!rows && JSON.stringify(q.data) !== JSON.stringify(rows),
    [q.data, rows],
  );
  const setRole = (i: number, role: AlgorithmRole) =>
    setRows((x) => (x ? x.map((row, j) => (j === i ? { ...row, algorithm_event_role: role } : row)) : x));

  return (
    <>
      <StSectionHead
        title="Event mapping"
        desc="Map each provider event NAME to one of the 11 canonical algorithm roles. Per app, provider-independent. New event names appear automatically as the ingester sees them; the provider is set in Settings → Integrations."
        right={
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {provider && <Badge kind="accent">{provider} events</Badge>}
            {dirty && (
              <Btn
                variant="primary"
                icon={I.check}
                disabled={replace.isPending}
                onClick={() => replace.mutate({ app_id: appId as number, rows: r })}
              >
                {replace.isPending ? "Saving…" : "Save mapping"}
              </Btn>
            )}
            <select
              className="select"
              value={appId ?? ""}
              onChange={(e) => setAppId(e.target.value ? Number(e.target.value) : null)}
              style={{ minWidth: 200 }}
            >
              <option value="">— pick app —</option>
              {(apps.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name ?? a.bundle_id}
                </option>
              ))}
            </select>
          </div>
        }
      />

      {appId == null && (
        <div className="card card-pad muted" style={{ textAlign: "center" }}>
          Pick an app to configure event mapping.
        </div>
      )}

      {appId != null && r.length === 0 && (
        <div className="card card-pad muted" style={{ textAlign: "center" }}>
          No provider events for this app yet. They appear here as the ingester
          records them (or after seeding).
        </div>
      )}

      {appId != null && r.length > 0 && (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="table-scroll">
            <table className="dt" style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  <th style={{ width: "42%" }}>Event name</th>
                  <th style={{ width: "38%" }}>Algorithm role</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {r.map((row, i) => (
                  <tr key={`${row.provider}:${row.provider_event_name}`}>
                    <td>
                      <span className="mono" style={{ fontSize: 12.5 }}>
                        {row.provider_event_name}
                      </span>
                    </td>
                    <td>
                      <select
                        className="select"
                        value={row.algorithm_event_role}
                        onChange={(e) => setRole(i, e.target.value as AlgorithmRole)}
                        style={{ width: 280 }}
                      >
                        {LTV_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {row.algorithm_event_role === "ignore" ? (
                        <Badge kind="paused">ignored</Badge>
                      ) : (
                        <Badge kind="enabled">
                          <span className="dot" />
                          mapped
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div
            className="muted"
            style={{ fontSize: 12, padding: "12px 20px", lineHeight: 1.5, borderTop: "1px solid var(--c-divider)" }}
          >
            Canonical roles:{" "}
            {LTV_ROLES.map((role, i) => (
              <span key={role} className="mono" style={{ fontSize: 11 }}>
                {role}
                {i < LTV_ROLES.length - 1 ? " · " : ""}
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Coefficients — three-row model (loaded / factual / predict + chosen source)
// ---------------------------------------------------------------------------
function fmtCoef(v: number | null): string {
  return v == null ? "—" : v.toFixed(4);
}

function SourcePill({
  label,
  value,
  active,
  disabled,
  title,
  onClick,
}: {
  label: string;
  value: number | null;
  active: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={() => !disabled && onClick()}
      style={{
        textAlign: "left",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        padding: "6px 10px",
        borderRadius: 8,
        minWidth: 96,
        border: active
          ? "1.5px solid var(--c-accent-500)"
          : "1px solid var(--c-border, var(--c-divider))",
        background: active ? "var(--c-accent-50)" : "var(--c-surface)",
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          textTransform: "uppercase",
          letterSpacing: ".04em",
          color: active ? "var(--c-accent-700)" : "var(--c-text-3)",
        }}
      >
        {label}
      </div>
      <div className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>
        {fmtCoef(value)}
      </div>
    </button>
  );
}

// §2 of the design — the raw uploaded curves (loaded_ltv_coefficients),
// visible immediately (independent of the weekly job / three-row model).
function LoadedCoefficientsTable({ appId }: { appId: number }) {
  const q = useLoadedCoefficients(appId);
  const [country, setCountry] = useState("");
  const [ctype, setCtype] = useState("");
  const rowsData = q.data?.rows;
  const total = q.data?.total ?? 0;

  const filtered = useMemo(
    () =>
      (rowsData ?? []).filter(
        (r) =>
          (!ctype || r.coefficient_type === ctype) &&
          (!country ||
            (country.toLowerCase() === "global"
              ? r.segment_level === "global"
              : (r.country ?? "").toUpperCase().includes(country.toUpperCase()))),
      ),
    [rowsData, country, ctype],
  );
  const shown = filtered.slice(0, 500);

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div
        className="card-head"
        style={{
          padding: "14px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h3>Loaded coefficients · per subtype</h3>
          <span className="muted">Your uploaded curves — {total} rows.</span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <select
            className="select"
            value={ctype}
            onChange={(e) => setCtype(e.target.value)}
            style={{ height: 30 }}
          >
            <option value="">all types</option>
            <option value="renewal">renewal</option>
            <option value="trial_to_paid">trial_to_paid</option>
          </select>
          <input
            className="input mono"
            placeholder="country (US / global)"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            style={{ width: 170, height: 30 }}
          />
        </div>
      </div>
      {q.isLoading ? (
        <div className="card-pad muted">Loading…</div>
      ) : total === 0 ? (
        <div className="card-pad muted">
          No loaded coefficients for this app yet.
        </div>
      ) : (
        <>
          <div className="table-scroll" style={{ maxHeight: 440 }}>
            <table className="dt" style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th>type</th>
                  <th>billing</th>
                  <th>trial</th>
                  <th>level</th>
                  <th>country</th>
                  <th>keyword</th>
                  <th className="num">from</th>
                  <th className="num">to</th>
                  <th className="num">coefficient</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r, i) => (
                  <tr key={i}>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {r.coefficient_type}
                    </td>
                    <td>{r.billing_period ?? "—"}</td>
                    <td>{r.has_trial == null ? "—" : r.has_trial ? "yes" : "no"}</td>
                    <td>{r.segment_level}</td>
                    <td>{r.country ?? "—"}</td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {r.keyword_text ?? "—"}
                    </td>
                    <td className="num mono">{r.from_day}</td>
                    <td className="num mono">{r.to_day}</td>
                    <td className="num mono" style={{ fontWeight: 600 }}>
                      {r.coefficient.toFixed(4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length > shown.length && (
            <div
              className="muted"
              style={{ padding: "10px 20px", fontSize: 12, borderTop: "1px solid var(--c-divider)" }}
            >
              Showing {shown.length} of {filtered.length} matching rows — narrow
              the filter to see more.
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function LtvCoefficientsSection({
  controlledAppId,
}: {
  controlledAppId?: number | null;
} = {}) {
  const apps = useApps();
  const [stickyAppId, setAppId] = useStickyAppId(apps.data);
  // When embedded in the merged Coefficients & Goals screen, the app comes from
  // the parent's single shared picker; standalone, we own the picker.
  const controlled = controlledAppId !== undefined;
  const appId = controlled ? controlledAppId : stickyAppId;

  const q = useCoefficientRows(appId);
  const setSource = useSetChosenSource();
  const rows = q.data ?? [];

  const pick = (r: CoefficientRow, src: CoefficientSource) => {
    if (appId == null || r.chosen_source === src) return;
    setSource.mutate({
      app_id: appId,
      picks: [
        {
          coefficient_type: r.coefficient_type,
          segment_level: r.segment_level,
          country: r.country,
          keyword_text: r.keyword_text,
          from_day: r.from_day,
          goal_day: r.goal_day,
          chosen_source: src,
        },
      ],
    });
  };
  const renewalRows = rows.filter((r) => r.coefficient_type === "renewal");
  const convRows = rows.filter((r) => r.coefficient_type === "trial_to_paid");
  const [drill, setDrill] = useState<CoefficientRow | null>(null);

  return (
    <>
      <StSectionHead
        title="Coefficients"
        desc="Per-segment renewal coefficients — Loaded (your uploaded curve), Factual (learned from matured cohorts), Predict (extrapolated). Click a cell to choose which source the autopilot uses for that segment; Factual is disabled until it's complete. The weekly auto-learning job fills these in."
        right={
          controlled ? undefined : (
            <select
              className="select"
              value={appId ?? ""}
              onChange={(e) => setAppId(e.target.value ? Number(e.target.value) : null)}
              style={{ minWidth: 200 }}
            >
              <option value="">— pick app —</option>
              {(apps.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name ?? a.bundle_id}
                </option>
              ))}
            </select>
          )
        }
      />

      {appId == null && (
        <div className="card card-pad muted" style={{ textAlign: "center" }}>
          Pick an app to view coefficients.
        </div>
      )}

      {appId != null && <LoadedCoefficientsTable appId={appId} />}

      {appId != null && (
        <div style={{ fontSize: 13.5, fontWeight: 600, margin: "20px 0 8px" }}>
          Three-row model{" "}
          <span className="muted" style={{ fontWeight: 400, fontSize: 12.5 }}>
            · Loaded vs Factual vs Predict — choose the source per segment.
            Trial → paid conversion has no Predict (it is observed early).
          </span>
        </div>
      )}

      {appId != null && rows.length > 0 && (
        <div
          className="muted"
          style={{
            fontSize: 12,
            lineHeight: 1.5,
            margin: "0 0 10px",
            padding: "8px 12px",
            border: "1px solid var(--c-border)",
            borderRadius: 8,
            background: "var(--c-surface-alt, transparent)",
          }}
        >
          <b style={{ color: "var(--c-text-2)" }}>Reading the Window column</b> —{" "}
          each row is one <code>D{"{anchor}"}→D{"{goal}"}</code> pair. The
          coefficient multiplies the <b>base LTV measured at the anchor day</b>{" "}
          to project revenue at the goal day. The anchor is fixed by the subtype,
          not by your metric&rsquo;s prediction day:
          <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
            <li>
              <b>Renewal</b> — anchor <code>D0</code> for no-trial subtypes (base =
              real first-payment revenue already present at install) or{" "}
              <code>D{"{trial duration}"}</code> for trial subtypes (base = real
              first-payment ARPPU once the trial converts). Both rest on a{" "}
              <b>real</b> base, never a derived one.
            </li>
            <li>
              <b>Trial → paid</b> — <code>D0→D{"{trial duration}"}</code>: the
              share of trials that convert, observed directly (no extrapolation).
            </li>
          </ul>
        </div>
      )}

      {appId != null && rows.length === 0 && (
        <div className="card card-pad muted" style={{ textAlign: "center", minHeight: 120 }}>
          No factual/predict yet — the weekly auto-learning job builds these
          from matured cohorts once you enable it in Advanced. Your uploaded
          Loaded curves are shown in the table above.
        </div>
      )}

      {appId != null && renewalRows.length > 0 && (
        <ThreeRowTable
          title="Renewal"
          rows={renewalRows}
          showPredict
          onPick={pick}
          onDrill={setDrill}
        />
      )}
      {appId != null && convRows.length > 0 && (
        <ThreeRowTable
          title="Trial → paid conversion"
          rows={convRows}
          showPredict={false}
          onPick={pick}
        />
      )}
      {appId != null && drill && (
        <SubtypeBreakdownModal appId={appId} row={drill} onClose={() => setDrill(null)} />
      )}
    </>
  );
}

// §4A subtype breakdown — drill-in from a Renewal segment row. Table only
// (survival curves live on the backend): per-subtype payers + share% +
// loaded / factual / predict, plus a TOTAL that mirrors the aggregate row.
function SubtypeBreakdownModal({
  appId,
  row,
  onClose,
}: {
  appId: number;
  row: CoefficientRow;
  onClose: () => void;
}) {
  const q = useSubtypeBreakdown(appId, {
    segment_level: row.segment_level,
    country: row.country,
    keyword_text: row.keyword_text,
    from_day: row.from_day,
    goal_day: row.goal_day,
  });
  const data = q.data ?? [];
  const fmt = (v: number | null) => (v == null ? "—" : v.toFixed(2));
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(15,18,30,.45)",
        zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 620, maxWidth: "94vw", maxHeight: "84vh", overflow: "auto",
          background: "var(--c-surface)", borderRadius: 14, padding: 20,
          boxShadow: "var(--sh-3)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
            Subtype breakdown
          </h3>
          <button className="btn icon sm" onClick={onClose} title="Close">×</button>
        </div>
        <div className="muted mono" style={{ fontSize: 12, margin: "4px 0 12px" }}>
          {row.country}
          {row.keyword_text ? ` / ${row.keyword_text}` : ""} · D{row.from_day}→D{row.goal_day}
        </div>
        {q.isLoading ? (
          <div className="muted" style={{ padding: 20, textAlign: "center" }}>Loading…</div>
        ) : data.length === 0 ? (
          <div className="muted" style={{ padding: 20, textAlign: "center" }}>
            No per-subtype data yet — the weekly job fills this once the segment
            has matured payers.
          </div>
        ) : (
          <table className="dt" style={{ minWidth: 560 }}>
            <thead>
              <tr>
                <th>Subtype</th>
                <th className="num">Payers</th>
                <th className="num">Share</th>
                <th className="num">Loaded</th>
                <th className="num">Factual</th>
                <th className="num">Predict</th>
              </tr>
            </thead>
            <tbody>
              {data.map((s) => (
                <tr key={s.subtype_key}>
                  <td style={{ fontWeight: 600 }}>{s.subtype_key}</td>
                  <td className="num">{s.payers}</td>
                  <td className="num">{s.share_pct.toFixed(0)}%</td>
                  <td className="num mono">{fmt(s.loaded_coefficient)}</td>
                  <td className="num mono">
                    {fmt(s.factual_coefficient)}
                    {s.factual_coefficient != null && s.factual_matured_to_day != null && (
                      <span className="muted" style={{ fontSize: 11 }}>
                        {" "}→W{Math.floor(s.factual_matured_to_day / 7)}
                      </span>
                    )}
                  </td>
                  <td className="num mono">
                    {fmt(s.predict_coefficient)}
                    {s.predict_extrapolated && (
                      <span className="muted" style={{ fontSize: 11 }}> ✳</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// The three-row (Loaded / Factual / Predict) picker table, one per coefficient
// type. Trial → paid has no Predict source (§4.4 — conversion is observed early,
// nothing to extrapolate), so `showPredict` drops that column.
function ThreeRowTable({
  title,
  rows,
  showPredict,
  onPick,
  onDrill,
}: {
  title: string;
  rows: CoefficientRow[];
  showPredict: boolean;
  onPick: (r: CoefficientRow, src: CoefficientSource) => void;
  onDrill?: (r: CoefficientRow) => void;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        className="muted"
        style={{ fontSize: 12, fontWeight: 600, margin: "10px 0 6px" }}
      >
        {title}
      </div>
      <div className="card">
        <div className="table-scroll">
          <table className="dt" style={{ minWidth: showPredict ? 720 : 560 }}>
            <thead>
              <tr>
                <th>Segment</th>
                <th
                  title={
                    "Anchor day → goal day. The coefficient multiplies the base LTV " +
                    "measured at the anchor day to reach the goal day. The anchor is " +
                    "the subtype anchor (D0 no-trial / D{trial} trial), not your " +
                    "metric's prediction day."
                  }
                >
                  Window
                </th>
                <th>Loaded</th>
                <th>Factual</th>
                {showPredict && <th>Predict</th>}
                {onDrill && <th></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={`${r.coefficient_type}:${r.segment_level}:${r.country}:${r.keyword_text ?? ""}:${r.from_day}:${r.goal_day}`}
                >
                  <td>
                    <span style={{ fontWeight: 600 }}>{r.country}</span>
                    {r.keyword_text && (
                      <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>
                        / {r.keyword_text}
                      </span>
                    )}
                    {r.flag && (
                      <span
                        title={
                          r.flag === "switch_candidate"
                            ? "Factual diverges from the active value — candidate for a switch (auto after 2 consecutive weekly runs outside the 2×CI corridor, shift ≤ 20%)."
                            : "Segment first crossed the learning base threshold — qualification started."
                        }
                        style={{
                          marginLeft: 6, fontSize: 10, fontWeight: 700,
                          padding: "1px 6px", borderRadius: 8,
                          background: r.flag === "switch_candidate" ? "#fef3c7" : "#dcfce7",
                          color: r.flag === "switch_candidate" ? "#92400e" : "#166534",
                        }}
                      >
                        {r.flag === "switch_candidate" ? "candidate" : "matured"}
                      </span>
                    )}
                    {r.chosen_auto && (
                      <span
                        title="Consumed source was switched automatically (rule 2.1)."
                        style={{
                          marginLeft: 4, fontSize: 10, fontWeight: 700,
                          padding: "1px 6px", borderRadius: 8,
                          background: "#e0e7ff", color: "#3730a3",
                        }}
                      >
                        auto
                      </span>
                    )}
                  </td>
                  <td
                    className="mono"
                    style={{ fontSize: 12 }}
                    title={
                      r.coefficient_type === "trial_to_paid"
                        ? `Trial→paid conversion over D${r.from_day}→D${r.goal_day} ` +
                          "(goal day = trial duration). Observed directly, no extrapolation."
                        : `Anchor day D${r.from_day} → goal day D${r.goal_day}. ` +
                          `Multiplies the real first-payment base (${
                            r.from_day === 0 ? "revenue present at install, no-trial" : "ARPPU once the trial converts"
                          }) measured at D${r.from_day} to project renewal revenue at D${r.goal_day}.`
                    }
                  >
                    D{r.from_day}→D{r.goal_day}
                  </td>
                  <td>
                    <SourcePill
                      label="Loaded"
                      value={r.loaded_coefficient}
                      active={r.chosen_source === "loaded"}
                      onClick={() => onPick(r, "loaded")}
                    />
                  </td>
                  <td>
                    <SourcePill
                      label="Factual"
                      value={r.factual_coefficient}
                      active={r.chosen_source === "factual"}
                      disabled={!r.factual_complete}
                      title={
                        r.factual_complete
                          ? undefined
                          : "Factual not complete yet — needs enough matured payers"
                      }
                      onClick={() => onPick(r, "factual")}
                    />
                  </td>
                  {showPredict && (
                    <td>
                      <SourcePill
                        label="Predict"
                        value={r.predict_coefficient}
                        active={r.chosen_source === "predict"}
                        disabled={r.predict_coefficient == null}
                        title={
                          r.predict_coefficient == null
                            ? "Predict not available for this segment yet"
                            : r.predict_extrapolated
                              ? "Extrapolated via the reference tail curve"
                              : undefined
                        }
                        onClick={() => onPick(r, "predict")}
                      />
                    </td>
                  )}
                  {onDrill && (
                    <td className="num">
                      <button
                        className="btn sm ghost"
                        onClick={() => onDrill(r)}
                        title="Per-subtype breakdown"
                      >
                        Subtypes ▸
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
